/**
 * cleansingKind.ts
 * ─────────────────
 * Single source of truth for deriving a display "kind" from a token block.
 *
 * Extracted from CleansingPatternDashboard so that DMTreeGridSummary's
 * field-level filter uses exactly the same logic as the dashboard counter.
 * Any divergence between "what the chart counts" and "what the filter shows"
 * is a bug — this module makes that impossible by sharing one function.
 *
 * Import in both files:
 *   import { deriveKind, ENGINEER_KINDS } from '../dq/cleansingKind';
 */

export function deriveKind(blk: any): string {
  const dslFn = (blk?.dsl_fn || '').toLowerCase();

  // Composite / conditional — each is its own kind, no decomposition.
  if (dslFn === 'chain_transforms')  return 'chain_transforms';
  if (dslFn === 'multi_map_replace') return 'multi_map_replace';
  if (dslFn === 'apply_if')          return 'apply_if';

  // Direct value mapping.
  if (dslFn === 'map_values')        return 'map_values';

  // regex_replace: kind comes from the first pair's classification.
  // literal_list is surfaced as 'synonym_group' for dashboard display.
  // IMPORTANT: the old field-filter code forgot this synonym_group remapping,
  // causing "Synonym group" clicks to show 0 results.
  if (dslFn === 'regex_replace') {
    const cls = blk?.regex_pairs?.[0]?.classification;
    if (!cls)                          return 'regex_replace';
    if (cls.kind === 'literal_list')   return 'synonym_group';
    return cls.kind || 'regex_replace';
  }

  // All other single-step transforms: normalize_case, trim, pad_left, etc.
  // Return dslFn directly; the dashboard KIND_META has entries for all of them.
  return dslFn || 'unknown';
}

/**
 * Kinds that require regex/engineering knowledge to edit.
 * Everything else is classified as SME-editable.
 *
 * chain_transforms and multi_map_replace are SME-editable because the
 * ChainStepsEditor gives per-step toggle/edit without raw regex knowledge.
 */
export const ENGINEER_KINDS = new Set<string>(['regex_raw']);